import React, { useState, useEffect, useMemo, memo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Breadcrumb from '../components/breadcrumb';
import HeaderSection from '../components/jobsite-details/HeaderSection';
import JobDetailsSection from '../components/jobsite-details/JobDetailsSection';
import AddressSection from '../components/jobsite-details/AddressSection';
// import WorkersSection from '../components/jobsite-details/WorkersSection';
// import FilesSection from '../components/jobsite-details/FilesSection';
// import TradesSection from '../components/jobsite-details/TradesSection';
import CommentsSection from '../components/jobsite-details/CommentsSection';
import MarkCompleteDialog from '../components/jobsite-details/MarkCompleteDialog';
import ApprovalDialog from '../components/jobsite-details/ApprovalDialog';
import AddCommentDialog from '../components/jobsite-details/AddCommentDialog';
import DeleteJobsiteDialog from '../components/jobsite-details/DeleteJobsiteDialog';
import RestoreJobsiteDialog from '../components/jobsite-details/RestoreJobsiteDialog';
import ConfirmCloseDialog from '../components/jobsite-details/ConfirmCloseDialog';
import { JobsiteDetailsSkeleton } from '../components/Skeleton';
import {
    JobsiteService,
    JobsiteWorkersDTO,
    JobsiteAddressDTO,
    JobsiteAddressUpdateDTO
} from '../services/jobsite.service';
import { TradeCount, TradeEntry } from '../types/jobsite-details.types';
import { toast } from 'react-toastify';
import googleMapsService from '../services/GoogleMaps.service';
import { authService } from '../services/auth.service';
import { getJobsiteManagementAccess } from '../config/roleAccess';
import { LogsService } from '../services/logs.service';
import { useJobsiteData } from '../hooks/useJobsiteData';
import { useComments } from '../hooks/useComments';
import { useEditForm } from '../hooks/useEditForm';
import JobsiteVisitsSection from '../components/jobsite-details/JobsiteVisitsSection';
import RecentJobsiteVisitsSection from '../components/jobsite-details/RecentJobsiteVisitsSection';
import { getJobsiteCompletionPercentage } from '../utils/jobsiteUtils';

// Navigation Component
const Navigation = memo(({ onBack }: { onBack: () => void }) => (
    <div className="mb-6 flex items-center">
        <ArrowLeft
            className="text-gray-600 mr-2 w-5 h-5 cursor-pointer"
            onClick={onBack}
            id="back-button"
        />
        <Breadcrumb title="Jobsite Details" />
    </div>
));
Navigation.displayName = 'Navigation';

const JobsiteDetails: React.FC = () => {
    const navigate = useNavigate();
    const params = useParams();
    const id = params.id;

    // Dialog states
    const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);
    const [showMarkCompleteDialog, setShowMarkCompleteDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [deleteReason, setDeleteReason] = useState('');
    const [deleteReasonError, setDeleteReasonError] = useState('');
    const [showRestoreDialog, setShowRestoreDialog] = useState(false);
    const [restoreReason, setRestoreReason] = useState('');
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [latestAction, setLatestAction] = useState<string>('');

    // Custom hooks
    const {
        jobsiteData,
        setJobsiteData,
        jobsiteTradeData,
        jobsiteFiles,
        setAddressId,
        refreshJobsiteFiles
    } = useJobsiteData(id, showEditDialog);

    const {
        comments,
        isLoadingComments,
        showCommentDialog,
        setShowCommentDialog,
        commentText,
        setCommentText,
        commentError,
        setCommentError,
        isSubmitting,
        handleCommentSubmit: handleCommentSubmitHook
    } = useComments(id);

    const {
        editFormData,
        setEditFormData,
        // sectors,
        // industries,
        // fundingResources,
        // provinces,
        trades,
        // contractors,
        // completionSteps,
        currentStep,
        setCurrentStep,
        // errors,
        // setErrors,
        // isProcessing,
        setIsProcessing,
        // processingText,
        setProcessingText,
        tradeCounts,
        setTradeCounts,
        tradeEntries,
        setTradeEntries,
        unionTypes,
        setUnionTypes,
        isSignificant,
        setIsSignificant,
        selectedLocals,
        setSelectedLocals,
        // showAddContractorDialog,
        // setShowAddContractorDialog,
        // contractorFormData,
        // contractorFormErrors,
        validateStep1: validateStep1Hook,
        validateStep2: validateStep2Hook,
        validateStep3: validateStep3Hook,
        validateStep5: validateStep5Hook,
        // handleContractorInputChange: handleContractorInputChangeHook,
        // handleAddContractor: handleAddContractorHook
    } = useEditForm();

    // Get user role and permissions
    const user = authService.getUser();
    const jobsiteAccess = user ? getJobsiteManagementAccess(user.role) : { addJobsite: false, deleteJobsite: false, restoreJobsite: false };

    const getStatusCompletionPercentage = (status: string): string => {
        const percentage = getJobsiteCompletionPercentage(status);
        return `${status} (${percentage}%)`;
    };

    // Define fetchLatestAction
    const fetchLatestAction = async () => {
        if (!id) return;
        try {
            const response = await LogsService.getJobsiteLogs({
                jobId: id,
                page: 1,
                limit: 1
            });

            if (response.data && response.data.length > 0) {
                const latestLog = response.data[0];
                let userDetails;
                userDetails = { name: latestLog.userName || 'Unknown User' };
                const formattedDate = new Date(latestLog.timeStamp).toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                });

                setLatestAction(`${userDetails.name} ${latestLog.actionType === 'CREATE' ? 'created' : latestLog.actionType === 'DELETE' ? 'deleted' : 'updated'} ${latestLog.entityType} | ${formattedDate}.`);
            } else {
                setLatestAction('No recent actions found.');
            }
        } catch (error) {
            console.error('Error fetching latest action:', error);
            setLatestAction('No recent actions found.');
        }
    };

    useEffect(() => {
        if (id) {
            fetchLatestAction();
        }
    }, [id]);

    const handleMarkCompleted = async () => {
        if (!id || jobsiteData?.jobDetails[0].markAsComp) return;
        setShowMarkCompleteDialog(true);
    };

    const confirmMarkCompleted = async () => {
        if (!id || jobsiteData?.jobDetails[0].markAsComp) return;

        try {
            await JobsiteService.markascomplete(id);
            const response = await JobsiteService.getJobsiteById(id);
            setJobsiteData(response);
            setShowMarkCompleteDialog(false);
            setIsApprovalDialogOpen(true);
        } catch (error) {
            console.error('Error marking jobsite as complete:', error);
            toast.error('Failed to mark jobsite as complete');
        }
    };

    // const handleEditJobsite = () => {
    //     if (jobsiteData?.jobDetails?.[0]) {
    //         const jobDetail = jobsiteData.jobDetails[0];
    //         const address = jobsiteData.address?.[0] || {};
    //         const workers = jobsiteData.workers?.[0] || {};
    //         const tradeType = jobsiteTradeData?.jobTrade?.[0]?.tradeType;

    //         setEditFormData({
    //             jobSiteName: jobDetail.jobSiteName || '',
    //             subsite: jobDetail.subsite || '',
    //             contact: jobDetail.siteContact || '',
    //             superintendent: jobDetail.siteContact || '',
    //             siteContactEmail: jobDetail.siteContactEmail || '',
    //             sector: jobDetail.sector?.id || '',
    //             industry: jobDetail.industry?.id || '',
    //             fundingResource: jobDetail.fundingResource?.id || '',
    //             activityStatus: jobDetail.status || 'active',
    //             completionStatus: jobDetail.jobCompletionStatus?.status || 'start',
    //             completionStep: jobDetail.jobCompletionStatus?.id || 'start',
    //             address1: address.address1 || '',
    //             address2: address.address2 ?? null,
    //             city: address.city || '',
    //             state: address.province?.id || '',
    //             postalCode: address.postalCode || '',
    //             workerLabel: workers.labels || [],
    //             workerCount: workers.noOfWorkers || '',
    //             workersType: workers.worksType || 'union',
    //             tradesCount: jobsiteTradeData?.noOfTrades || '',
    //             tradeType: tradeType?.id || '',
    //             files: [],
    //             projectType: '',
    //             percentageCompleted: '',
    //             isUnion: false,
    //             contractor: jobDetail.contractor?.id || '',
    //             address: '',
    //             country: '',
    //             markAsComp: jobDetail.markAsComp || false
    //         });

    //         // Determine which step to show first based on incomplete data
    //         let initialStep = 1;

    //         const step1Complete = jobDetail.jobSiteName &&
    //             jobDetail.siteContact &&
    //             jobDetail.siteContactEmail &&
    //             jobDetail.sector?.id &&
    //             jobDetail.industry?.id &&
    //             jobDetail.fundingResource?.id &&
    //             jobDetail.contractor?.id;

    //         const step2Complete = address.address1 &&
    //             address.city &&
    //             address.postalCode &&
    //             address.province?.id;

    //         const step3Complete = workers.labels?.length > 0 &&
    //             workers.noOfWorkers &&
    //             workers.worksType;

    //         const step4Complete = jobsiteFiles.length > 0;

    //         const step5Complete = jobsiteTradeData?.noOfTrades &&
    //             jobsiteTradeData?.jobTrade?.length > 0 &&
    //             jobsiteTradeData?.jobTrade?.every(trade =>
    //                 trade.tradeContractAssociations?.every(assoc =>
    //                     assoc.contractor?.id &&
    //                     (assoc.noOfJourneymen !== undefined || assoc.noOfApperntices !== undefined)
    //                 )
    //             );

    //         if (!step1Complete) initialStep = 1;
    //         else if (!step2Complete) initialStep = 2;
    //         else if (!step3Complete) initialStep = 3;
    //         else if (!step4Complete) initialStep = 4;
    //         else if (!step5Complete) initialStep = 5;

    //         setCurrentStep(initialStep);
    //         setShowEditDialog(true);
    //     }
    // };

    // Helper function to handle completed jobsite error
    const handleCompletedJobsiteError = (error: any): boolean => {
        if (error.message === 'This jobsite has already been marked as completed and cannot be modified.' ||
            error.response?.data?.message === 'This jobsite has already been marked as completed and cannot be modified.') {
            toast.error('This jobsite has already been marked as completed and cannot be modified.');
            setShowEditDialog(false);
            return true;
        }
        return false;
    };

    const handleStepSubmit = async (nextStep?: number) => {
        if (!id) return;

        try {
            const user = authService.getUser();
            if (!user?.id) {
                toast.error('User information not found');
                return;
            }

            // Step 1: Job Details
            if (currentStep === 1) {
                if (!validateStep1Hook()) {
                    toast.error('Please fill in all required fields');
                    return;
                }

                const currentJobDetails = jobsiteData?.jobDetails?.[0];
                if (!currentJobDetails) {
                    toast.error('Current jobsite details not found');
                    return;
                }

                const currentData = {
                    jobSiteName: currentJobDetails.jobSiteName || '',
                    subsite: currentJobDetails.subsite || '',
                    siteContact: currentJobDetails.siteContact || '',
                    siteContactEmail: currentJobDetails.siteContactEmail || '',
                    sectors: JSON.stringify(currentJobDetails.jobDetailsSectors?.map((jds: any) => jds.sector?.id).sort() || []),
                    industry: currentJobDetails.industry?.id || '',
                    fundingResource: currentJobDetails.fundingResource?.id || '',
                    status: currentJobDetails.status as 'active' | 'inactive',
                    jobCompletionStatus: currentJobDetails.jobCompletionStatus?.id || 'start',
                    contractor: currentJobDetails.generalContractor?.id || currentJobDetails.contractor?.id || '',
                    hasPLA: currentJobDetails.hasPLA || false,
                    hasMRF: currentJobDetails.hasMRF || false
                };

                const newData = {
                    jobSiteName: editFormData.jobSiteName,
                    subsite: editFormData.subsite,
                    siteContact: editFormData.superintendent,
                    siteContactEmail: editFormData.siteContactEmail,
                    sectors: JSON.stringify([...(editFormData.sectors || [])].sort()),
                    industry: editFormData.industry,
                    fundingResource: editFormData.fundingResource,
                    status: editFormData.activityStatus as 'active' | 'inactive',
                    jobCompletionStatus: editFormData.completionStep,
                    contractor: editFormData.contractor,
                    hasPLA: editFormData.hasPLA || false,
                    hasMRF: editFormData.hasMRF || false
                };

                const hasChanges = Object.keys(newData).some(key =>
                    currentData[key as keyof typeof currentData] !== newData[key as keyof typeof newData]
                );

                if (!hasChanges) {
                    if (nextStep) {
                        setCurrentStep(nextStep);
                    } else {
                        setShowEditDialog(false);
                    }
                    return;
                }

                const updateData: any = {
                    jobSiteName: newData.jobSiteName,
                    subsite: newData.subsite,
                    siteContact: newData.siteContact,
                    siteContactEmail: newData.siteContactEmail,
                    sectors: editFormData.sectors,
                    industry: newData.industry || null,
                    fundingResource: newData.fundingResource,
                    status: newData.status,
                    jobCompletionStatus: newData.jobCompletionStatus,
                    markAsComp: false,
                    generalContractor: newData.contractor,
                    userId: user.id,
                    hasPLA: newData.hasPLA,
                    hasMRF: newData.hasMRF
                };

                try {
                    await JobsiteService.updateJobsiteDetails(id, updateData);

                    const updatedJobsiteDetails = await JobsiteService.getJobsiteById(id);
                    if (updatedJobsiteDetails) {
                        setJobsiteData(updatedJobsiteDetails);
                    }
                    await fetchLatestAction();
                    toast.success('Job details updated successfully!');
                } catch (stepError: any) {
                    if (handleCompletedJobsiteError(stepError)) return;
                    throw stepError;
                }
            }

            // Step 2: Address
            if (currentStep === 2) {
                if (!validateStep2Hook()) {
                    toast.error('Please fill in all required fields');
                    return;
                }

                try {
                    const currentAddressResponse = await JobsiteService.getJobsiteAddress(id);
                    let effectiveAddressId = '';
                    if (currentAddressResponse && currentAddressResponse.id) {
                        effectiveAddressId = currentAddressResponse.id;
                        setAddressId(currentAddressResponse.id);
                    } else {
                        setAddressId('');
                    }

                    let coordinates: number[] = [];

                    if (navigator.onLine) {
                        const google = await googleMapsService.loadMapsApi();
                        const geocoder = new google.maps.Geocoder();
                        const fullAddress = `${editFormData.address1}, ${editFormData.city}, ${editFormData.state}, ${editFormData.postalCode}, Canada`;

                        coordinates = await new Promise<number[]>((resolve, reject) => {
                            geocoder.geocode({ address: fullAddress }, (results: any, status: any) => {
                                if (status === 'OK' && results[0]) {
                                    const location = results[0].geometry.location;
                                    resolve([location.lat(), location.lng()]);
                                } else {
                                    reject(new Error('Geocoding failed'));
                                }
                            });
                        });
                    } else {
                        coordinates = currentAddressResponse?.coordinates?.coordinates || [];
                    }

                    type AddressComparisonData = {
                        address1: string | null;
                        address2: string | null;
                        city: string | null;
                        postalCode: string | null;
                        province: string | null;
                    };

                    const currentData: AddressComparisonData = {
                        address1: currentAddressResponse?.address1 || null,
                        address2: currentAddressResponse?.address2 || null,
                        city: currentAddressResponse?.city || null,
                        postalCode: currentAddressResponse?.postalCode || null,
                        province: currentAddressResponse?.province?.id || null,
                    };

                    const newData: AddressComparisonData = {
                        address1: editFormData.address1 || null,
                        address2: editFormData.address2 || null,
                        city: editFormData.city || null,
                        postalCode: editFormData.postalCode || null,
                        province: editFormData.state || null,
                    };

                    const hasChanges = Object.keys(newData).some(key =>
                        currentData[key as keyof AddressComparisonData] !== newData[key as keyof AddressComparisonData]
                    );

                    if (!hasChanges) {
                        if (nextStep) {
                            setCurrentStep(nextStep);
                        } else {
                            setShowEditDialog(false);
                        }
                        return;
                    }

                    const addressUpdateData: JobsiteAddressDTO = {
                        address1: newData.address1 || '',
                        address2: newData.address2 ?? null,
                        city: newData.city || '',
                        postalCode: newData.postalCode || '',
                        province: newData.province || '',
                        coordinates: coordinates,
                        userId: user.id,
                        job_id: id
                    };

                    try {
                        if (effectiveAddressId) {
                            const updateData: JobsiteAddressUpdateDTO = {
                                ...addressUpdateData,
                                address2: !editFormData.address2 ? null : editFormData.address2.trim() || null
                            };
                            await JobsiteService.updateJobsiteAddress(effectiveAddressId, updateData);
                        } else {
                            const created = await JobsiteService.createJobsiteAddress(addressUpdateData);
                            if (created && created.id) {
                                setAddressId(created.id);
                            }
                        }

                        const updatedJobsiteDetails = await JobsiteService.getJobsiteById(id);
                        if (updatedJobsiteDetails) {
                            setJobsiteData(updatedJobsiteDetails);
                        }
                        await fetchLatestAction();
                        toast.success('Address updated successfully!');
                    } catch (stepError: any) {
                        if (handleCompletedJobsiteError(stepError)) return;
                        throw stepError;
                    }
                } catch (error: any) {
                    if (handleCompletedJobsiteError(error)) return;
                    console.error('Error in address update process:', error);
                    toast.error(error.message);
                }
            }

            // Step 3: Workers
            if (currentStep === 3) {
                if (!validateStep3Hook()) {
                    toast.error('Please fill in all required fields');
                    return;
                }

                try {
                    const currentWorkersResponse = await JobsiteService.getJobsiteWorkers(id) as any;

                    const currentData = {
                        labels: (currentWorkersResponse.labels as string[]) || [],
                        noOfWorkers: (currentWorkersResponse.noOfWorkers as string) || '',
                        worksType: (currentWorkersResponse.worksType as 'union' | 'non-union' | 'mixed') || 'union'
                    };

                    const newData = {
                        labels: editFormData.workerLabel,
                        noOfWorkers: editFormData.workerCount,
                        worksType: editFormData.workersType,
                    };

                    const hasChanges = Object.keys(newData).some(key => {
                        if (key === 'labels') {
                            const currentLabels = currentData.labels;
                            const newLabels = newData.labels;
                            return currentLabels.length !== newLabels.length ||
                                currentLabels.some((label: string, index: number) => label !== newLabels[index]);
                        }
                        return currentData[key as keyof typeof currentData] !== newData[key as keyof typeof newData];
                    });

                    if (!hasChanges) {
                        if (nextStep) {
                            setCurrentStep(nextStep);
                        } else {
                            setShowEditDialog(false);
                        }
                        return;
                    }

                    const workersData: JobsiteWorkersDTO = {
                        labels: newData.labels,
                        noOfWorkers: newData.noOfWorkers,
                        worksType: newData.worksType as 'union' | 'non-union' | 'mixed',
                        userId: user.id,
                        job_id: id
                    };

                    try {
                        const workerId = currentWorkersResponse.id as string;
                        if (workerId) {
                            await JobsiteService.updateJobsiteWorkers(workerId, workersData);
                        } else {
                            await JobsiteService.createJobsiteWorkers(workersData);
                        }

                        const updatedJobsiteDetails = await JobsiteService.getJobsiteById(id);
                        if (updatedJobsiteDetails) {
                            setJobsiteData(updatedJobsiteDetails);
                        }
                        await fetchLatestAction();
                        toast.success('Workers information updated successfully!');
                    } catch (stepError: any) {
                        if (handleCompletedJobsiteError(stepError)) return;
                        throw stepError;
                    }
                } catch (error:any) {
                    if (handleCompletedJobsiteError(error)) return;
                    console.error('Error in workers update process:', error);
                    toast.error(error.message);
                }
            }

            // Step 4: Files
            if (currentStep === 4) {
                if (editFormData.files.length > 0) {
                    try {
                        await JobsiteService.updateJobsiteFiles(id, user.id, editFormData.files);

                        setEditFormData(prev => ({
                            ...prev,
                            files: []
                        }));

                        await refreshJobsiteFiles();
                        await fetchLatestAction();
                        toast.success('Files uploaded successfully!');
                    } catch (error: any) {
                        if (handleCompletedJobsiteError(error)) return;
                        console.error('Error uploading files:', error);
                        toast.error(error.message);
                        return;
                    }
                }
            }

            // Step 5: Trades
            if (currentStep === 5) {
                try {
                    // First, get current data to check for changes before validation and processing
                    const currentTradesResponse = await JobsiteService.getJobsiteTrades(id);
                    const tradeId = currentTradesResponse?.id;

                    // Build current data for comparison
                    const currentData = {
                        noOfTrades: currentTradesResponse?.noOfTrades || '',
                        trades: (currentTradesResponse?.jobTrade || [])
                            .map(trade => ({
                                tradeTypeId: trade.tradeType?.id || '',
                                unionStatus: trade.unionStatus || 'union',
                                isSignatory: trade.isSignatory || false,
                                localUnionIds: (trade.localUnionIds || []).map(local => local.id).sort(),
                                numOfWorkers: trade.numOfWorkers || 0,
                                isUACanadaTrade: trade.isUACanadaTrade || false,
                                associations: (trade.tradeContractAssociations || [])
                                    .filter(assoc => assoc.contractor?.id && (assoc.noOfJourneymen > 0 || assoc.noOfApperntices > 0))
                                    .map(assoc => ({
                                        contractorId: assoc.contractor?.id || '',
                                        noOfJourneymen: assoc.noOfJourneymen || 0,
                                        noOfApperntices: assoc.noOfApperntices || 0
                                    })).sort((a, b) => a.contractorId.localeCompare(b.contractorId))
                            }))
                            .filter(trade => trade.associations.length > 0)
                            .sort((a, b) => a.tradeTypeId.localeCompare(b.tradeTypeId))
                    };

                    // Build new data for comparison
                    const selectedTradeIds = editFormData.tradeType.split(',').filter(Boolean);
                    const newData = {
                        noOfTrades: editFormData.tradesCount,
                        trades: selectedTradeIds.map(tradeId => {
                            const trade = trades.find(t => t.id === tradeId);
                            const tradeAssociations = tradeEntries[tradeId] || [];
                            const unionStatus = unionTypes[tradeId] || 'union';
                            const isUATrade = trade?.category === 'UA';
                            const isTradeSignificant = isSignificant[tradeId] === 'yes';
                            const localUnionId = selectedLocals[tradeId];
                            const numOfWorkers = !isUATrade ? parseInt(tradeCounts.find(t => t.tradeId === tradeId)?.workers || '0', 10) : 0;

                            const validTradeAssociations = tradeAssociations
                                .filter(entry => entry.contractorId && (entry.journeymen > 0 || entry.apprentices > 0))
                                .map(entry => ({
                                    contractorId: entry.contractorId,
                                    noOfJourneymen: entry.journeymen,
                                    noOfApperntices: entry.apprentices
                                })).sort((a, b) => a.contractorId.localeCompare(b.contractorId));

                            return {
                                tradeTypeId: tradeId,
                                unionStatus: unionStatus as 'union' | 'non-union' | 'mixed',
                                isSignatory: isTradeSignificant,
                                localUnionIds: isUATrade && isTradeSignificant && localUnionId ? [localUnionId].sort() : [],
                                numOfWorkers,
                                isUACanadaTrade: isUATrade,
                                associations: validTradeAssociations
                            };
                        }).filter(trade => trade.associations.length > 0).sort((a, b) => a.tradeTypeId.localeCompare(b.tradeTypeId))
                    };

                    // Deep comparison function for trades
                    const areTradesEqual = (current: typeof currentData, newTradeData: typeof newData): boolean => {
                        if (current.noOfTrades !== newTradeData.noOfTrades) return false;
                        if (current.trades.length !== newTradeData.trades.length) return false;

                        return current.trades.every((currentTrade, index) => {
                            const newTrade = newTradeData.trades[index];
                            if (!newTrade) return false;

                            return currentTrade.tradeTypeId === newTrade.tradeTypeId &&
                                currentTrade.unionStatus === newTrade.unionStatus &&
                                currentTrade.isSignatory === newTrade.isSignatory &&
                                currentTrade.numOfWorkers === newTrade.numOfWorkers &&
                                currentTrade.isUACanadaTrade === newTrade.isUACanadaTrade &&
                                JSON.stringify(currentTrade.localUnionIds) === JSON.stringify(newTrade.localUnionIds) &&
                                currentTrade.associations.length === newTrade.associations.length &&
                                currentTrade.associations.every((currentAssoc, assocIndex) => {
                                    const newAssoc = newTrade.associations[assocIndex];
                                    return newAssoc &&
                                        currentAssoc.contractorId === newAssoc.contractorId &&
                                        currentAssoc.noOfJourneymen === newAssoc.noOfJourneymen &&
                                        currentAssoc.noOfApperntices === newAssoc.noOfApperntices;
                                });
                        });
                    };

                    const hasChanges = !areTradesEqual(currentData, newData);

                    if (!hasChanges) {
                        console.log('No changes detected in trades, skipping API call');
                        if (nextStep) {
                            setCurrentStep(nextStep);
                        } else {
                            setShowEditDialog(false);
                        }
                        return;
                    }

                    // Only validate and start processing if changes are detected
                    if (!validateStep5Hook()) {
                        toast.error('Please fill up all the required fields');
                        return;
                    }

                    setIsProcessing(true);
                    setProcessingText('Saving trades information...');

                    const selectedTrades = trades.filter((trade: any) => editFormData.tradeType.split(',').includes(trade.id));
                    
                    const tradesData = {
                        userId: user.id,
                        job_id: id,
                        noOfTrades: newData.noOfTrades,
                        jobTrade: selectedTrades.map((trade: any) => {
                            const tradeAssociations = tradeEntries[trade.id] || [];
                            const unionStatus = unionTypes[trade.id] || 'union';
                            const isUATrade = trades.find((t: any) => t.id === trade.id)?.category === 'UA';
                            const isTradeSignificant = isSignificant[trade.id] === 'yes';
                            const localUnionId = selectedLocals[trade.id];
                            const numOfWorkers = !isUATrade ? parseInt(tradeCounts.find((t: any) => t.tradeId === trade.id)?.workers || '0', 10) : 0;

                            const validTradeAssociations = tradeAssociations
                                .filter(entry => entry.contractorId && (entry.journeymen > 0 || entry.apprentices > 0))
                                .map(entry => ({
                                    contractorId: entry.contractorId,
                                    tradeType: trade.id,
                                    noOfJourneymen: entry.journeymen,
                                    noOfApperntices: entry.apprentices
                                }));

                            return {
                                tradesType: trade.id,
                                unionStatus: unionStatus as 'union' | 'non-union' | 'mixed',
                                isSignatory: isTradeSignificant,
                                localUnionIds: isUATrade && isTradeSignificant && localUnionId ? [localUnionId] : [],
                                numOfWorkers,
                                isUACanadaTrade: isUATrade,
                                tradeContractAssociation: validTradeAssociations
                            };
                        }).filter(trade => trade.tradeContractAssociation.length > 0)
                    };

                    try {
                        if (tradeId) {
                            await JobsiteService.updateJobsiteTrades(tradeId, tradesData);
                            await JobsiteService.getJobsites({ page: '1', limit: '10' });
                        } else {
                            await JobsiteService.createJobsiteTraders(tradesData);
                            await JobsiteService.getJobsites({ page: '1', limit: '10' });
                        }

                        await fetchLatestAction();
                        toast.success('Trades information updated successfully!');
                        setShowEditDialog(false);
                    } catch (stepError: any) {
                        if (handleCompletedJobsiteError(stepError)) return;
                        throw stepError;
                    }
                } catch (error:any) {
                    if (handleCompletedJobsiteError(error)) return;
                    console.error('Error in trades update process:', error);
                    toast.error(error.message);
                } finally {
                    setIsProcessing(false);
                    setProcessingText('');
                }
                return;
            }

            if (!nextStep) {
                try {
                    setShowEditDialog(false);
                    setEditFormData({
                        jobSiteName: '',
                        subsite: '',
                        contact: '',
                        projectType: '',
                        percentageCompleted: '',
                        isUnion: false,
                        contractor: '',
                        address: '',
                        city: '',
                        country: '',
                        state: '',
                        postalCode: '',
                        workerLabel: [],
                        workerCount: '',
                        superintendent: '',
                        siteContactEmail: '',
                        sectors: [],
                        industry: '',
                        fundingResource: '',
                        activityStatus: 'active',
                        completionStatus: 'start',
                        completionStep: 'start',
                        address1: '',
                        address2: '',
                        workersType: 'union',
                        tradesCount: '',
                        tradeType: '',
                        files: [],
                        markAsComp: false
                    });
                    setCurrentStep(1);
                    setTradeCounts([]);
                    setTradeEntries({});
                    setUnionTypes({});
                    setIsSignificant({});
                    setSelectedLocals({});
                    setCurrentStep(1);

                    const response = await JobsiteService.getJobsiteById(id);
                    setJobsiteData(response);
                } catch (error: any) {
                    console.error('Error updating jobsite:', error);
                    toast.error(error.message);
                }
            } else {
                setCurrentStep(nextStep);
            }
        } catch (error: any) {
            if (handleCompletedJobsiteError(error)) return;
            console.error('Error updating jobsite:', error);
            toast.error(error.message);
        }
    };

    const handleEditSubmit = () => handleStepSubmit(undefined);

    const handleViewInMap = () => {
        navigate('/jobsite-management', {
            state: {
                viewMode: 'map',
                jobsiteId: id,
                preserveFilters: true
            }
        });
    };
    const formatAddress = useMemo(() => {
        if (!jobsiteData?.address?.[0]) return '';

        const address = jobsiteData.address[0];

        const parts = [
            address.address1,
            address.address2,
            address.city,
            address.province?.name,
            address.postalCode
        ].filter(Boolean);

        return parts.join(', ');
    }, [jobsiteData?.address]);

    const handleDeleteJobsite = async () => {
        setShowDeleteDialog(true);
    };

    const confirmDeleteJobsite = async () => {
        if (!id) return;

        if (!deleteReason.trim()) {
            setDeleteReasonError('Please provide a reason for deletion');
            return;
        }

        const user = authService.getUser();
        if (!user?.id) {
            toast.error('User information not found');
            return;
        }

        try {
            const response = await JobsiteService.deleteJobsite(id, user.id, deleteReason);
            toast.success(response.message);
            navigate('/jobsite-management');
        } catch (error) {
            console.error('Error deleting jobsite:', error);
            toast.error('Failed to delete jobsite. Please try again.');
        } finally {
            setShowDeleteDialog(false);
            setDeleteReason('');
            setDeleteReasonError('');
        }
    };

    const handleRestoreJobsite = () => {
        setShowRestoreDialog(true);
    };

    const confirmRestoreJobsite = async () => {
        if (!id) return;

        const user = authService.getUser();
        if (!user?.id) {
            toast.error('User information not found');
            return;
        }

        try {
            const response = await JobsiteService.restoreJobsite(
                id,
                user.id,
                restoreReason.trim() || undefined
            );
            toast.success(response.message);

            // Refresh jobsite data to show updated status
            const updatedJobsite = await JobsiteService.getJobsiteById(id);
            setJobsiteData(updatedJobsite);
            setShowRestoreDialog(false);
            setRestoreReason('');
        } catch (error) {
            console.error('Error restoring jobsite:', error);
            toast.error('Failed to restore jobsite. Please try again.');
        } finally {
            setShowRestoreDialog(false);
            setRestoreReason('');
        }
    };

    const handleBack = () => {
        navigate('/jobsite-management', {
            state: {
                viewMode: 'list',
                back: true,
                preserveFilters: true
            }
        });
    };

    const handleViewFile = (fileId: string) => {
        const fileUrl = `https://your-api-endpoint.com/files/${fileId}`;
        window.open(fileUrl, '_blank');
    };

    const handleTradeEntryChange = (tradeId: string, entries: TradeEntry[]) => {
        setTradeEntries(prev => ({
            ...prev,
            [tradeId]: entries
        }));
    };

    useEffect(() => {
        const scrollableContainer = document.querySelector('.scrollable-container');
        if (scrollableContainer) {
            scrollableContainer.scrollTop = 0;
        }
    }, [currentStep]);

    // Helper functions for file preview
    const getMimeType = (fileName: string): string => {
        const extension = fileName.toLowerCase().split('.').pop();
        const mimeTypes: { [key: string]: string } = {
            'jpg': 'image/jpeg',
            'jpeg': 'image/jpeg',
            'png': 'image/png',
            'gif': 'image/gif',
            'bmp': 'image/bmp',
            'webp': 'image/webp',
            'svg': 'image/svg+xml',
            'pdf': 'application/pdf',
            'doc': 'application/msword',
            'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'xls': 'application/vnd.ms-excel',
            'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'ppt': 'application/vnd.ms-powerpoint',
            'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            'txt': 'text/plain',
            'csv': 'text/csv',
            'heif': 'image/heif',
            'tiff': 'image/tiff',
            'tif': 'image/tiff'
        };

        return mimeTypes[extension || ''] || 'application/octet-stream';
    };

    const handlePreviewFile = async (attachmentId: string, fileName: string) => {
        try {
            // Fetch file using attachment ID (secure approach)
            const blobData = await JobsiteService.viewFile(attachmentId) as Blob;

            const mimeType = getMimeType(fileName);
            const blob = new Blob([blobData], { type: mimeType });

            const url = window.URL.createObjectURL(blob);
            const newTab = window.open(url, '_blank');

            setTimeout(() => {
                URL.revokeObjectURL(url);
            }, 1000);

            if (!newTab) {
                toast.error('Please allow popups for this website to preview files');
            }
        } catch (error) {
            console.error('Preview failed', error);
            toast.error('Failed to preview file');
        }
    };

    // const handlePreviewLocalFile = (file: File) => {
    //     try {
    //         const mimeType = getMimeType(file.name);
    //         const blob = new Blob([file], { type: mimeType });

    //         const url = window.URL.createObjectURL(blob);
    //         const newTab = window.open(url, '_blank');

    //         setTimeout(() => {
    //             URL.revokeObjectURL(url);
    //         }, 1000);

    //         if (!newTab) {
    //             toast.error('Please allow popups for this website to preview files');
    //         }
    //     } catch (error) {
    //         console.error('Preview failed', error);
    //         toast.error('Failed to preview file');
    //     }
    // };

    // const handleDeleteFile = async (fileId: string) => {
    //     try {
    //         await JobsiteService.deleteJobsiteFile(fileId);
    //         await refreshJobsiteFiles();
    //         toast.success('File deleted successfully');
    //     } catch (error) {
    //         console.error('Error deleting file:', error);
    //         toast.error('Failed to delete file. Please try again.');
    //     }
    // };

    // Add handler for trade selection
    // const handleTradeSelect = (selected: string[]) => {
    //     const newTradeCounts = selected
    //         .filter(tradeId => !tradeCounts.find(t => t.tradeId === tradeId))
    //         .map(tradeId => ({
    //             tradeId,
    //             tradeName: trades.find(t => t.id === tradeId)?.name || '',
    //             journeymen: 0,
    //             apprentices: 0,
    //             entries: [],
    //             workers: '0'
    //         }));

    //     const filteredTradeCounts = tradeCounts.filter(t => selected.includes(t.tradeId));
    //     setTradeCounts([...filteredTradeCounts, ...newTradeCounts]);
    //     setEditFormData(prev => ({ ...prev, tradeType: selected.join(',') }));
    // };

    // const handleCountUpdate = (tradeId: string, type: 'journeymen' | 'apprentices', value: string) => {
    //     const numValue = parseInt(value) || 0;
    //     setTradeCounts(prev => prev.map(trade =>
    //         trade.tradeId === tradeId
    //             ? { ...trade, [type]: numValue }
    //             : trade
    //     ));
    // };

    // const handleRemoveTrade = (tradeId: string) => {
    //     const updatedCounts = tradeCounts.filter(t => t.tradeId !== tradeId);
    //     setTradeCounts(updatedCounts);

    //     const updatedTradeIds = updatedCounts.map(t => t.tradeId);
    //     setEditFormData(prev => ({
    //         ...prev,
    //         tradeType: updatedTradeIds.join(',')
    //     }));
    // };

    // Initialize tradeCounts from jobsiteTradeData when editing
    useEffect(() => {
        if (showEditDialog && jobsiteTradeData) {
            const initialTradeCounts: TradeCount[] = (jobsiteTradeData?.jobTrade || []).map(trade => {
                const associations = trade.tradeContractAssociations || [];

                const entries = associations.map(assoc => ({
                    contractorId: assoc.contractor.id,
                    journeymen: assoc.noOfJourneymen,
                    apprentices: assoc.noOfApperntices
                }));

                setTradeEntries(prev => ({
                    ...prev,
                    [trade.tradeType.id]: entries
                }));

                setUnionTypes(prev => ({
                    ...prev,
                    [trade.tradeType.id]: trade.unionStatus
                }));

                setIsSignificant(prev => ({
                    ...prev,
                    [trade.tradeType.id]: trade.isSignatory ? 'yes' : 'no'
                }));

                if (trade.localUnionIds?.length > 0 && trade.localUnionIds[0]) {
                    setSelectedLocals(prev => ({
                        ...prev,
                        [trade.tradeType.id]: trade.localUnionIds[0].id
                    }));
                }

                return {
                    tradeId: trade.tradeType.id,
                    tradeName: trade.tradeType.name,
                    journeymen: associations.reduce((sum, assoc) => sum + assoc.noOfJourneymen, 0),
                    apprentices: associations.reduce((sum, assoc) => sum + assoc.noOfApperntices, 0),
                    contractorId: associations[0]?.contractor.id,
                    entries: entries,
                    workers: trade?.numOfWorkers?.toString() || '0'
                };
            });

            setTradeCounts(initialTradeCounts);

            const updatedTradeTypeIds = (jobsiteTradeData?.jobTrade || [])
                .map(trade => trade.tradeType?.id || '')
                .filter(Boolean)
                .join(',');

            setEditFormData(prev => ({
                ...prev,
                tradeType: updatedTradeTypeIds,
                tradesCount: jobsiteTradeData.noOfTrades || prev.tradesCount
            }));
        } else {
            setTradeCounts([]);
            setTradeEntries({});
            setUnionTypes({});
            setIsSignificant({});
            setSelectedLocals({});
        }
    }, [showEditDialog, jobsiteTradeData]);

    if (!jobsiteData || !jobsiteData.jobDetails[0]) {
        return (
            <div className="bg-gray-50 p-3 sm:p-6">
                <Navigation onBack={handleBack} />
                <JobsiteDetailsSkeleton />
            </div>
        );
    }

    const jobDetails = jobsiteData.jobDetails[0];
    const workers = jobsiteData.workers[0];
    const jobsiteVisits = jobsiteData.visits;

    // Get the latest visit (visits are ordered by createdAt DESC from backend)
    const latestVisit = jobsiteVisits && jobsiteVisits.length > 0 ? jobsiteVisits[0] : null;

    return (
        <div className="bg-gray-50 p-3 sm:p-6">
            <Navigation onBack={handleBack} />
            <div>
                <HeaderSection
                    jobDetails={jobDetails}
                    formatAddress={formatAddress}
                    latestAction={latestAction}
                    jobsiteAccess={jobsiteAccess}
                    id={id || ''}
                    getCompletionPercentage={getJobsiteCompletionPercentage}
                    handleViewInMap={handleViewInMap}
                    // handleEditJobsite={handleEditJobsite}
                    handleMarkCompleted={handleMarkCompleted}
                    handleDeleteJobsite={handleDeleteJobsite}
                    handleRestoreJobsite={handleRestoreJobsite}
                    userRole={authService.getUser()?.role || ''}
                />

                {latestVisit && (
                    <>
                        <JobsiteVisitsSection
                            title="Latest Jobsite Visit"
                            visitDate={latestVisit?.visitDate}
                            totalWorkers={latestVisit?.totalWorkers}
                            jobDetails={jobDetails}
                            jobsiteVisitDetails={{
                                jobCompletionStatus: { status: latestVisit?.jobCompletionStatus?.status || 'Start' },
                                comments: latestVisit?.comments?.map((c: { comment: string }) => c.comment) || []
                            }}
                            jobsiteFiles={latestVisit?.jobFiles || []}
                            jobsiteTradeData={latestVisit?.jobTrades || null}
                            getStatusCompletionPercentage={getStatusCompletionPercentage}
                            getCompletionPercentage={getJobsiteCompletionPercentage}
                            handleViewFile={handleViewFile}
                        />
                        <RecentJobsiteVisitsSection jobId={jobDetails.id} />
                    </>
                )}
                
                <JobDetailsSection jobDetails={jobDetails} getStatusCompletionPercentage={getStatusCompletionPercentage} />
                <AddressSection jobsiteData={jobsiteData} />
                <CommentsSection
                    comments={comments}
                    isLoadingComments={isLoadingComments}
                    showCommentDialog={showCommentDialog}
                    setShowCommentDialog={setShowCommentDialog}
                    commentText={commentText}
                    setCommentText={setCommentText}
                    commentError={commentError}
                    setCommentError={setCommentError}
                    handleCommentSubmit={() => handleCommentSubmitHook(fetchLatestAction)}
                    isSubmitting={isSubmitting}
                    userRole={authService.getUser()?.role || ''}
                />
            </div>

            {/* All Dialogs */}
            <MarkCompleteDialog
                isOpen={showMarkCompleteDialog}
                onClose={() => setShowMarkCompleteDialog(false)}
                onConfirm={confirmMarkCompleted}
            />

            <ApprovalDialog
                isOpen={isApprovalDialogOpen}
                onClose={() => setIsApprovalDialogOpen(false)}
            />

            <AddCommentDialog
                isOpen={showCommentDialog}
                onClose={() => setShowCommentDialog(false)}
                onSubmit={() => handleCommentSubmitHook(fetchLatestAction)}
                commentText={commentText}
                setCommentText={setCommentText}
                commentError={commentError}
                setCommentError={setCommentError}
                isSubmitting={isSubmitting}
            />

            <DeleteJobsiteDialog
                isOpen={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                onConfirm={confirmDeleteJobsite}
                deleteReason={deleteReason}
                setDeleteReason={setDeleteReason}
                deleteReasonError={deleteReasonError}
                setDeleteReasonError={setDeleteReasonError}
            />

            <RestoreJobsiteDialog
                isOpen={showRestoreDialog}
                onClose={() => setShowRestoreDialog(false)}
                onConfirm={confirmRestoreJobsite}
                restoreReason={restoreReason}
                setRestoreReason={setRestoreReason}
            />

            <ConfirmCloseDialog
                isOpen={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                onConfirm={() => {
                    setShowEditDialog(false);
                    setShowConfirmDialog(false);
                }}
            />
        </div>
    );
};

export default JobsiteDetails;